import { call, put, takeLatest, select } from 'redux-saga/effects';
import moment from 'moment';
import { GetCirculationFilters } from 'types';
import { YieldOf } from 'types/utils';
import {
  fetchCirculations,
  fetchedCirculations,
  refreshCirculations,
  refreshedCirculations,
  exportCirculations,
  exportedCirculations,
  setPersistedCirculationsFilters,
} from './actions';
import { logout } from '../api-auth/actions';
import getAPI from '../../api/iv-back';
import { shoudUsePersistedCirculationsFiltersSelector } from './selectors';

type FetchCirculationsActionPayload = ReturnType<typeof fetchCirculations>;
type RefreshCirculationsActionPayload = ReturnType<typeof refreshCirculations>;
type ExportCirculationsPayload = ReturnType<typeof exportCirculations>;

export function* updatePersistedCirculationsFilters(values: GetCirculationFilters) {
  const shoudUsePersistedCirculationsFilters: boolean = yield select(shoudUsePersistedCirculationsFiltersSelector);
  if (!shoudUsePersistedCirculationsFilters) {
    yield put(setPersistedCirculationsFilters(values));
  }
}

function* fetchCirculationsSaga({ payload }: FetchCirculationsActionPayload) {
  const { getCirculations } = getAPI();
  const response: YieldOf<typeof getCirculations> = yield call(getCirculations, payload);
  const { data, ok, problem, status } = response;
  yield call(updatePersistedCirculationsFilters, payload);
  if (status === 401 || status === 403) {
    yield put(logout());
  }
  if (ok) {
    yield put(fetchedCirculations({ ...data, searchTimestamp: moment().format() }));
  } else {
    const error = problem || 'unknown';
    yield put(fetchedCirculations(undefined, error));
  }
}

function* refreshCirculationsSaga({ payload }: RefreshCirculationsActionPayload) {
  const { refreshCirculationList } = getAPI();
  const response: YieldOf<typeof refreshCirculationList> = yield call(refreshCirculationList, payload);
  const { data, ok, problem } = response;
  yield call(updatePersistedCirculationsFilters, payload);
  if (ok) {
    yield put(refreshedCirculations({ ...data, searchTimestamp: moment().format() }));
  } else {
    const error = problem || 'unknown';
    yield put(refreshedCirculations(undefined, error));
  }
}

function* exportCirculationsSaga({ payload, meta }: ExportCirculationsPayload) {
  const { exportCirculationList } = getAPI();
  const response: YieldOf<typeof exportCirculationList> = yield call(exportCirculationList, payload, meta.language);
  const { data, ok, problem, status } = response;
  yield call(updatePersistedCirculationsFilters, payload);
  if (status === 401 || status === 403) {
    yield put(logout());
  }
  if (ok) {
    yield put(exportedCirculations(data));
  } else {
    const error = problem || 'unknown';
    yield put(exportedCirculations(undefined, error));
  }
}

export default function* circulationsSaga() {
  yield takeLatest(fetchCirculations.type, fetchCirculationsSaga);
  yield takeLatest(refreshCirculations.type, refreshCirculationsSaga);
  yield takeLatest(exportCirculations.type, exportCirculationsSaga);
}
